export const ANALYTICS_CONSENT_STORAGE_KEY = "byfolio.analytics-consent"
export const ANALYTICS_CONSENT_SCHEMA_VERSION = 1

export type AnalyticsConsentChoice = "unset" | "accepted" | "rejected"

interface PersistedAnalyticsConsent {
  readonly version: number
  readonly choice: Exclude<AnalyticsConsentChoice, "unset">
}

interface StorageReaderWriter {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

interface StorageEvents {
  addEventListener(
    type: "storage",
    listener: (event: StorageEvent) => void
  ): void
  removeEventListener(
    type: "storage",
    listener: (event: StorageEvent) => void
  ): void
}

type ConsentListener = (choice: AnalyticsConsentChoice) => void

export const parseStoredAnalyticsConsent = (
  serialized: string | null
): AnalyticsConsentChoice => {
  if (serialized === null) return "unset"

  try {
    const value: unknown = JSON.parse(serialized)

    if (
      typeof value === "object" &&
      value !== null &&
      "version" in value &&
      value.version === ANALYTICS_CONSENT_SCHEMA_VERSION &&
      "choice" in value &&
      (value.choice === "accepted" || value.choice === "rejected")
    ) {
      return value.choice
    }
  } catch {
    // Invalid or inaccessible state is intentionally treated as no consent.
  }

  return "unset"
}

export class AnalyticsConsentStore {
  private choice: AnalyticsConsentChoice = "unset"
  private readonly listeners = new Set<ConsentListener>()
  private listening = false

  constructor(
    private readonly storage?: StorageReaderWriter,
    private readonly storageEvents?: StorageEvents
  ) {}

  initialize(): AnalyticsConsentChoice {
    try {
      this.choice = parseStoredAnalyticsConsent(
        this.storage?.getItem(ANALYTICS_CONSENT_STORAGE_KEY) ?? null
      )
    } catch {
      this.choice = "unset"
    }

    if (!this.listening && this.storageEvents) {
      this.storageEvents.addEventListener("storage", this.handleStorage)
      this.listening = true
    }

    return this.choice
  }

  getChoice(): AnalyticsConsentChoice {
    return this.choice
  }

  setChoice(choice: Exclude<AnalyticsConsentChoice, "unset">): void {
    const value: PersistedAnalyticsConsent = {
      version: ANALYTICS_CONSENT_SCHEMA_VERSION,
      choice,
    }

    try {
      this.storage?.setItem(
        ANALYTICS_CONSENT_STORAGE_KEY,
        JSON.stringify(value)
      )
    } catch {
      // The in-memory decision remains effective when storage is unavailable.
    }

    this.update(choice)
  }

  subscribe(listener: ConsentListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  destroy(): void {
    if (this.listening && this.storageEvents) {
      this.storageEvents.removeEventListener("storage", this.handleStorage)
      this.listening = false
    }
    this.listeners.clear()
  }

  private readonly handleStorage = (event: StorageEvent): void => {
    if (event.key !== null && event.key !== ANALYTICS_CONSENT_STORAGE_KEY)
      return
    this.update(parseStoredAnalyticsConsent(event.newValue))
  }

  private update(choice: AnalyticsConsentChoice): void {
    if (choice === this.choice) return
    this.choice = choice
    this.listeners.forEach(listener => {
      listener(choice)
    })
  }
}
